<template>
  <div
    class="event"
    :class="{ passive: !event.active, embedded: embeddedMode }"
  >
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-document"
      @close="closeFindBar"
    />
    <div id="scrollable-document">
      <EventTopBar
        v-if="!embeddedMode"
        :event="event"
        :has-block-active="hasBlockActive"
        with-right-panel
        @select-all="selectAllDocument"
        @flush="flush"
        @find="openFindBar"
      >
        <template #actions>
          <RecordingToolbar
            v-bind="recordingToolbarProps"
            @recording-control-click="onRecordingControlClick"
            @toggle-transcript="toggleTranscriptPopover"
            @close-transcript="closeRecordingTranscript"
          />
        </template>
      </EventTopBar>
      <div class="infos" @keydown="keydown">
        <TitledDocument
          ref="document"
          :key="event.id"
          :class="{ hidden: !event.attendable }"
          class="titled-document"
          :debounce="debounce"
          :debounce-title="0"
          :editable-title="event.editable"
          :templates="eventTemplates"
          :value="event"
          :embedded-mode="embeddedMode"
        >
          <template #aboveTitle>
            <DefaultTypeChip v-if="!embeddedMode" kind="event" />
          </template>

          <template #properties>
            <div class="event-properties">
              <CalendarTag
                v-model:value="calendarId"
                :editable="event.movable"
                display="icon-only"
              />
              <EventSourceIcon :calendar="calendar" />
              <DateAndTime
                v-model:value="time"
                :editable="event.editable && (isOrganizer || isCreator)"
                class="progress"
                :edited="propsEdited.time"
              />
              <ButtonMenuRecurrence
                v-if="event.editable && (isOrganizer || isCreator)"
                v-model:recurrence="recurrence"
                :time="time"
                @custom="openCustomRecurrence"
              />
              <Recurrence v-else :value="recurrence" disabled compact />
            </div>
            <EventProgressBar
              class="progress-bar"
              :event="event"
              with-remaining
            />
          </template>

          <template #subTitle>
            <ExtendedProperties
              class="extension-field-list"
              object-type="event"
              :object-id="event.id"
              :extension-object-id="event.extension_row"
              :create-extension-object-callback="ensureEventExtensionObject"
            />
          </template>
        </TitledDocument>
        <!-- This should be deleted when we'll be capable of writing notes on unowned calendar's events.  -->
        <div v-if="!event.attendable" class="not-owned-event">
          <div class="content">
            <div class="title-wrapper">
              <div class="event-properties">
                <RTag
                  v-if="calendar"
                  :text="calendar.summary"
                  :theme="`calendar-${calendar.color}`"
                />
                <EventSourceIcon :calendar="calendar" />
                <DateAndTime v-model:value="time" class="progress" />
              </div>
              <h1 class="not-owned-title">
                {{ title }}
              </h1>
              <EventProgressBar
                class="progress-bar"
                :event="event"
                with-remaining
              />
            </div>
            <p>You cannot take notes for events in calendars you do not own.</p>
            <div class="illu-wrapper">
              <div class="illu-not-owned-event" />
            </div>
          </div>
        </div>
      </div>

      <div v-if="!embeddedMode" class="sidebar" @keydown="keydown">
        <div ref="moreElement" class="more">
          <div class="sidebar-row sidebar-row--conference">
            <BaseConferenceButton
              v-if="meeting && event.attendable"
              is-big
              :meeting-name="meeting.name"
              :meeting-icon="meeting.icon"
              @open-url="openUrl"
            />
            <div v-else class="empty-conference">
              <ConferenceIcon />
            </div>
          </div>

          <div
            v-if="calendarOwnerEmail"
            class="sidebar-row sidebar-row--attendees"
          >
            <div class="sidebar-icon">
              <PersonIcon />
            </div>
            <div class="sidebar-content">
              <AttendeesList
                v-model:attendees="attendees"
                :calendar-owner-email="calendarOwnerEmail"
                :is-attendable="event.attendable"
                :is-meeting-with-me="isMeetingWithMe"
                :event="event"
                :disabled="!event.editable"
              />
            </div>
          </div>

          <div
            v-if="event.editable || !!location"
            class="sidebar-row sidebar-row--location"
          >
            <div class="sidebar-icon">
              <LocationIcon />
            </div>
            <div class="sidebar-content">
              <Location
                v-model:value="location"
                :disabled="!event.editable"
                :edited="propsEdited.location"
              />
            </div>
            <div
              v-if="!!location"
              class="sidebar-action"
              @click="openLocationLink"
            >
              <ExternalArrowIcon />
            </div>
          </div>

          <div
            v-if="event.editable || !!description"
            class="sidebar-row sidebar-row--description"
          >
            <div class="sidebar-icon">
              <NotesIcon />
            </div>
            <div class="sidebar-content">
              <BaseInput
                v-if="event.editable && (!description || isEditingDescription)"
                ref="descriptionInput"
                v-model:value="description"
                class="description"
                placeholder="Add description"
                :is-disabled="!event.editable"
                :is-edited="propsEdited.description"
                :is-one-line="false"
                :textarea-default-rows="2"
                has-styles
                @focus="isEditingDescription = true"
                @blur="isEditingDescription = false"
              />
              <div
                v-else-if="!!description"
                class="description description-html"
                :class="{ editable: event.editable }"
                v-html="sanitizedDescription"
                @click="handleDescriptionClick"
              />
            </div>
          </div>

          <div
            v-if="(previousEvents?.length ?? 0) > 0"
            class="sidebar-row sidebar-row--events"
          >
            <div class="sidebar-icon">
              <ActivityIcon />
            </div>
            <div class="sidebar-content">
              <EventsList
                class="previous-events"
                :events="previousEvents ?? []"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="hasUnsavedChanges" class="bottom-toast">
        <span v-if="!isDraftEvent" class="text">{{ patchText }}</span>
        <BaseButton
          kind="auxiliary"
          :text="isDraftEvent ? 'Cancel' : 'Discard'"
          tint="red"
          @click="discard"
        />
        <BaseButton
          kind="auxiliary"
          :text="isDraftEvent ? 'Create' : 'Update'"
          tint="blue"
          @click="confirm"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useHead } from "@unhead/vue";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  provide,
  Ref,
  ref,
  toRefs,
  watch,
} from "vue";
import type { NavigationGuardNext } from "vue-router";
import { useRoute } from "vue-router";

import type {
  Event,
  EventId,
  EventPatch,
  FullEvent,
  ParentId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseConferenceButton from "@/components/base/BaseConferenceButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import ButtonMenuRecurrence from "@/components/ButtonMenuRecurrence.vue";
import DateAndTime from "@/components/DateAndTime.vue";
import { useDocument } from "@/components/document";
import AttendeesList from "@/components/elements/AttendeesList.vue";
import CalendarTag from "@/components/elements/CalendarTag.vue";
import DefaultTypeChip from "@/components/elements/DefaultTypeChip.vue";
import EventsList from "@/components/elements/EventsList.vue";
import EventSourceIcon from "@/components/elements/EventSourceIcon.vue";
import Location from "@/components/elements/Location.vue";
import RTag from "@/components/elements/RTag.vue";
import EventTopBar from "@/components/event/EventTopBar.vue";
import { useEventInfo } from "@/components/eventInfo";
import EventProgressBar from "@/components/EventProgressBar.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import ActivityIcon from "@/components/icons/ActivityIcon.vue";
import ConferenceIcon from "@/components/icons/ConferenceIcon.vue";
import ExternalArrowIcon from "@/components/icons/ExternalArrowIcon.vue";
import LocationIcon from "@/components/icons/LocationIcon.vue";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import * as inject from "@/components/inject";
import RecordingToolbar from "@/components/RecordingToolbar.vue";
import Recurrence from "@/components/Recurrence.vue";
import { eventTemplates } from "@/components/templates";
import TitledDocument from "@/components/TitledDocument.vue";
import { useAudioRecording } from "@/composables/recording/useAudioRecording";
import {
  STORAGE_KEY,
  useAutoMeetingRecording,
} from "@/composables/recording/useAutoMeetingRecording";
import { useMeetingRecording } from "@/composables/recording/useMeetingRecording";
import { useRecordingToolbar } from "@/composables/recording/useRecordingToolbar";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useConference } from "@/composables/useConference";
import { useExtendedProperties } from "@/composables/useExtendedProperties";
import { useScrollable } from "@/composables/useScrollable";
import { openLocation } from "@/event";
import { metrics } from "@/metrics";
import type { MeetingRecordingContext } from "@/recording/types";
import { useReleaser } from "@/ref";
import { keydown } from "@/routes/object";
import { html } from "@/utils/html";
import { navigateToCalendar } from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";

import ExtendedProperties from "../ExtendedProperties.vue";
import { PropsEdited, useEventDetails } from "../planner/controllers/event";

export default defineComponent({
  components: {
    ActivityIcon,
    AttendeesList,
    BaseButton,
    BaseConferenceButton,
    BaseInput,
    ButtonMenuRecurrence,
    CalendarTag,
    ConferenceIcon,
    DefaultTypeChip,
    ExternalArrowIcon,
    DateAndTime,
    EventProgressBar,
    ExtendedProperties,
    EventsList,
    EventSourceIcon,
    EventTopBar,
    FindBar,
    Location,
    LocationIcon,
    PersonIcon,
    Recurrence,
    RecordingToolbar,
    NotesIcon,
    RTag,
    TitledDocument,
  },
  props: {
    debounce: { type: Number, default: 5000 },
    event: { type: Object as PropType<FullEvent>, required: true },
    hasUnsavedChanges: { type: Boolean, required: true },
    isDraftEvent: { type: Boolean, required: true },
    patch: { type: Object as PropType<EventPatch>, required: true },
    propsEdited: {
      type: Object as PropType<PropsEdited>,
      required: true,
    },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    confirm: () => true,
    discard: (_nextRoute?: NavigationGuardNext) => true,
  },
  async setup(props, { emit }) {
    const { event, patch, isDraftEvent, hasUnsavedChanges } = toRefs(props);
    const router = inject.router();
    const route = useRoute();
    const controller = inject.controller();
    const profile = inject.profile();
    const calendars = inject.calendars();
    const logger = inject.logger("Event");
    const baseLogger = logger.base();
    const billing = inject.billing();
    const clock = inject.clock();

    const releaser = useReleaser();

    // Register the document element as a scrollable container
    const scrollable = useScrollable(["scrollable-document"]);
    onMounted(scrollable.mount);
    releaser.defer(scrollable.unmount);

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const audioRecording = releaser.add(useAudioRecording(baseLogger));
    const meetingRecordingContext = computed<MeetingRecordingContext>(() => ({
      id: event.value.id,
      title: event.value.title ?? "",
      target: {
        kind: "event",
        start: event.value.start,
        end: event.value.end,
        active: event.value.active,
        attendable: event.value.attendable,
        link: event.value.link,
      },
    }));
    const meetingRecording = useMeetingRecording(
      baseLogger,
      billing,
      audioRecording,
      meetingRecordingContext
    );
    const {
      status,
      isSilent,
      partialText,
      recordingError,
      recordButtonState,
      startOrResumeRecording,
      pauseRecording,
      resumeRecording,
      stopRecording,
    } = meetingRecording;

    const repeatModal = inject.repeatModal();
    // Namespaced per account (adopting the value written un-namespaced by
    // older versions on first read).
    const { migrate, syncState } = useBrowserStore(
      profile.value.user.accountId
    );
    migrate(STORAGE_KEY);
    const autoMeetingRecording = releaser.add(
      syncState<boolean>(STORAGE_KEY, true)
    );

    // If ?record=true is passed, force the recording to start (meeting prompt).
    // Keep reactive for navigations that reuse this component instance.
    const shouldForceStartRecording = ref(route.query.record === "true");
    watch(
      () => route.query.record,
      (record) => {
        if (record === "true") {
          shouldForceStartRecording.value = true;
        }
      }
    );
    // Clear the query param right away to avoid re-triggering the recording
    if (route.query.record !== undefined) {
      const query = { ...route.query };
      delete query.record;
      void router.replace({ query });
    }
    const {
      attendees,
      calendar: calendarId,
      description,
      location,
      recurrence,
      time,
      title,
    } = useEventDetails(event, controller);
    useHead({
      title: computed(() => title.value),
    });
    const document = ref<InstanceType<typeof TitledDocument> | null>(null);
    const makeParentId = (eventId: string): ParentId | null =>
      eventId.includes("#") ? null : { kind: "event", id: eventId };
    const previousEvents = ref<Event[]>([]);
    const parent = ref<ParentId | null>(makeParentId(event.value.id));
    provide("parent", parent);
    // Note: Vue does not support async computed
    const getPreviousEventsAndParent = async (eventId: EventId) => {
      await controller.event.withSameAttendees(eventId).then((events) => {
        previousEvents.value = events.filter(
          (e) => e.start.getTime() < event.value.start.getTime()
        );
      });
      parent.value = makeParentId(eventId);
    };
    watch(() => event.value.id, getPreviousEventsAndParent, {
      immediate: true,
    });
    const nextRoute: Ref<Function | null> = ref(null);
    const eventInfo = useEventInfo(controller, calendars, event);
    releaser.add(
      eventInfo.then((eventInfo) => ({
        release: () => eventInfo.releaseEventInfo(),
      }))
    );

    const {
      calendar,
      calendarOwnerEmail,
      isCreator,
      isMeeting,
      isMeetingWithMe,
      isOrganizer,
      meeting,
    } = await eventInfo;
    const moreElement: Ref<HTMLElement | null> = ref(null);
    const isPatchMeeting = computed<boolean>(
      () =>
        !!(
          (patch.value?.attendees && patch.value?.attendees.length > 1) ||
          isMeeting.value
        )
    );
    const patchText = computed(() =>
      isMeeting.value && !isOrganizer.value && !isCreator.value
        ? "Because you are not the organizer of this meeting, modifications will only apply to the event in your calendar."
        : `The ${isPatchMeeting.value ? "meeting" : "event"} has pending
    modifications.`
    );
    const ensureEventExtensionObject = useExtendedProperties(
      controller,
      profile,
      "event",
      () => event.value?.extension_row,
      (id: string) => {
        if (event.value) {
          event.value.extension_row = id;
        }
      },
      async (id: string) => {
        if (event.value) {
          await controller.event.update(
            { id: event.value.id, extension_row: id },
            false,
            "app"
          );
        }
      }
    );

    const discard = () => {
      document.value?.flush();
      const next: NavigationGuardNext = () => {
        if (nextRoute.value) nextRoute.value();
        else if (isDraftEvent.value)
          navigateToCalendar(router, event.value.start);
      };
      emit("discard", next);
    };
    const confirm = () => {
      document.value?.flush();
      emit("confirm");
    };

    const openUrl = () => {
      const conference = useConference(controller, event.value);
      if (conference) {
        conference.open();
      }
    };
    // This allows you to edit description/time etc without having the caret to refocus the title all the time
    const {
      flush,
      hasBlockActive,
      initializeFocus: initializeGenericFocus,
      selectAll: selectAllDocument,
    } = useDocument<FullEvent>(event, document, router);
    const initializeFocus = () => {
      if (!hasUnsavedChanges.value) {
        initializeGenericFocus();
      }
    };
    const openCustomRecurrence = () => repeatModal.value.showModal(event);

    // Send metrics
    releaser.defer(
      watch(recordButtonState, (newValue) => {
        switch (newValue) {
          case "idle":
            metrics.segment.track(`Meeting Recording Stopped`, {
              action: "stopped",
            });
            break;
          case "recording":
            metrics.segment.track(`Meeting Recording Started`, {
              action: "started",
            });
            break;
        }
      })
    );

    const {
      release: releaseAutoMeetingRecording,
      errorMessage: autoMeetingRecordingError,
      willAutoRecordEvent,
      recordingStartsInLabel,
      isStartingRecording,
      markAutoMeetingRecordinngStarted: markAutoAIMeetingRecordingStarted,
      markAutoMeetingRecordinngStopped: markAutoAIMeetingRecordingStopped,
    } = useAutoMeetingRecording(
      baseLogger,
      clock,
      meetingRecording,
      meetingRecordingContext,
      autoMeetingRecording,
      true,
      { forceStart: shouldForceStartRecording }
    );
    releaser.defer(releaseAutoMeetingRecording);
    const mergedRecordingError = computed(
      () => recordingError.value || autoMeetingRecordingError.value
    );
    const recordingAutoStartLabel = computed(() => {
      if (!willAutoRecordEvent.value || !recordingStartsInLabel.value)
        return null;
      return recordingStartsInLabel.value;
    });

    const getAutoMeetingRecordingError = () => autoMeetingRecordingError.value;
    const clearAutoMeetingRecordingError = () => {
      autoMeetingRecordingError.value = null;
    };

    const {
      release: releaseRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    } = useRecordingToolbar(meetingRecording, audioRecording, {
      getExtraRecordingError: getAutoMeetingRecordingError,
      onAfterStartRecording: markAutoAIMeetingRecordingStarted,
      onAfterStopRecording: markAutoAIMeetingRecordingStopped,
      clearErrorsOnToggle: clearAutoMeetingRecordingError,
      getStartPending: () => isStartingRecording.value,
      toolbar: {
        compact: true,
        tint: "red",
        transcriptAnchor: "right",
        recordLabel: "Record meeting",
        getAutoStartLabel: () => recordingAutoStartLabel.value,
      },
    });
    releaser.defer(releaseRecordingToolbar);

    const sanitizedDescription = computed(() =>
      html.sanitize(description.value)
    );

    const isEditingDescription = ref(false);
    const descriptionInput = ref<InstanceType<typeof BaseInput> | null>(null);

    const handleDescriptionClick = (e: MouseEvent) => {
      const target =
        e.target instanceof Element
          ? e.target
          : e.target instanceof Node
            ? e.target.parentElement
            : null;
      // Check if the clicked element or any parent is a link
      const link = target?.closest("a");
      if (link) {
        e.preventDefault();
        const href = link.getAttribute("href");
        if (href) {
          openExternalUrl(href, controller);
        }
      } else if (event.value.editable) {
        // Enter edit mode when clicking on the description (but not on links)
        isEditingDescription.value = true;
        // Focus the input on next tick after it's rendered
        setTimeout(() => {
          const input = descriptionInput.value;
          if (input && input.inputEl) {
            input.focus();
          }
        }, 0);
      }
    };

    const openLocationLink = () => {
      openLocation(location.value, controller);
    };

    return {
      attendees,
      calendar,
      calendarId,
      calendarOwnerEmail,
      closeFindBar,
      confirm,
      description,
      descriptionInput,
      discard,
      document,
      findBarEl,
      isFindBarOpen,
      openFindBar,
      eventTemplates,
      flush,
      handleDescriptionClick,
      hasBlockActive,
      initializeFocus,
      isCreator,
      isEditingDescription,
      isMeetingWithMe,
      isOrganizer,
      isPatchMeeting,
      keydown,
      location,
      meeting,
      moreElement,
      openCustomRecurrence,
      openLocationLink,
      openUrl,
      selectAllDocument,
      time,
      recurrence,
      title,
      sanitizedDescription,
      previousEvents,
      patchText,
      status,
      isSilent,
      partialText,
      recordingError: mergedRecordingError,
      start: startOrResumeRecording,
      pause: pauseRecording,
      resume: resumeRecording,
      stop: stopRecording,
      recordingAutoStartLabel,
      recordingStartsInLabel,
      recordButtonState,
      ensureEventExtensionObject,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/event.scss";
@use "@/styles/context-actions.scss";
@use "@/styles/_variables.scss" as *;

div.event {
  // Anchor for the find bar.
  position: relative;

  // Clear the fixed 330px sidebar.
  .find-bar {
    inset-inline-end: 350px;
  }

  #scrollable-document {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    overflow-y: scroll;
    scrollbar-width: none;
  }

  .infos {
    width: calc(100% - 330px);
    box-sizing: border-box;

    .event-properties {
      display: flex;
      flex-direction: row;
      column-gap: 8px;
      align-items: center;
    }

    .extension-field-list {
      margin-top: 6px;
      width: 100%;
    }

    .hidden {
      display: none;
    }

    .not-owned-event {
      width: 100%;
      box-sizing: border-box;

      .content {
        height: 100%;
        max-width: 1500px;
        margin: 16px auto 0;
        width: calc(100% - 192px);
        padding: 0 96px;
        display: flex;
        flex-direction: column;
        align-items: center;

        .title-wrapper {
          margin-bottom: 24px;
          padding-top: 30px;
          width: 100%;
          max-width: $document-max-width;

          h1 {
            @extend %font-h1;
            margin: 8px 0 0 0;

            @include themed() {
              color: t($primary);
            }
          }
        }

        p {
          width: 100%;
          max-width: $document-max-width;
          margin: 0;
          @extend %font-page-regular;

          @include themed() {
            color: t($grey-40);
          }
        }

        .illu-wrapper {
          width: 100%;
          max-width: $document-max-width;

          .illu-not-owned-event {
            margin: auto;
            margin-top: 80px;
            margin-inline-start: 42px;
            background-size: 334px 208px;
            width: 334px;
            height: 208px;

            @include themed() {
              background-image: t($illu-not-owned-event);
            }
          }
        }
      }
    }
  }

  &.embedded .infos {
    width: 100%;
  }

  &.passive .infos {
    .titled-document :deep(.title) .r-input .input,
    .not-owned-title {
      @include themed() {
        color: t($grey-50);
      }
    }
  }

  .progress-bar {
    margin-top: 12px;
  }

  .sidebar {
    padding-top: 24px;
    position: fixed;
    display: flex;
    flex-direction: column;
    inset-inline-end: 0;
    width: 330px;
    height: calc(100% - 24px);
    row-gap: 24px;

    @include themed() {
      background: t($bg-alt);
      border-inline-start: 1px solid t($grey-10);
    }

    .more {
      overflow-y: auto;
      z-index: $z-index-content;
      height: calc(100% - 32px);
      padding-bottom: 32px;
      display: flex;
      max-width: 100%;
      padding: 0 16px 24px 16px;
      flex-direction: column;
      row-gap: 24px;
      -ms-overflow-style: none;
      /* IE and Edge */
      scrollbar-width: none;

      /* Firefox */
      &::-webkit-scrollbar {
        display: none;
      }
    }

    .base-input.description {
      :deep(textarea) {
        @extend %font-caption-regular;
        height: auto;
        overflow-y: hidden;

        @include themed() {
          color: t($grey-50);
        }
      }
    }

    .description-html {
      @extend %font-small-regular;
      word-break: break-word;
      padding: 0 12px;

      @include themed() {
        color: t($grey-50);
      }

      &.editable {
        cursor: text;
        padding: 4px 8px;
        border: 1px solid transparent;
        border-radius: $border-radius-m;

        &:hover {
          @include themed() {
            border-color: t($grey-30);
          }
        }
      }

      :deep(p) {
        margin: 0 0 8px 0;

        &:last-child {
          margin-bottom: 0;
        }
      }

      :deep(a) {
        text-decoration: underline;
        cursor: pointer;
        @include transition(color 100ms ease-out);
        @include themed() {
          color: t($grey-40);
        }

        &:hover {
          @include themed() {
            color: t($grey-70);
          }
        }
      }

      :deep(strong) {
        font-weight: 600;
      }

      :deep(ul) {
        margin: 0 0 8px 0;
        padding-inline-start: 20px;
        list-style-type: disc;
        list-style-position: outside;
      }

      :deep(ol) {
        margin: 0 0 8px 0;
        padding-inline-start: 20px;
        list-style-type: decimal;
        list-style-position: outside;
      }

      :deep(li) {
        display: list-item;
        margin-bottom: 4px;
      }

      :deep(h1),
      :deep(h2),
      :deep(h3),
      :deep(h4),
      :deep(h5),
      :deep(h6) {
        margin: 8px 0;
        font-weight: 600;
      }

      :deep(code) {
        @extend %font-small-regular;
        padding: 2px 4px;
        border-radius: 3px;

        @include themed() {
          background-color: t($grey-05);
          color: t($primary);
        }
      }

      :deep(pre) {
        margin: 8px 0;
        padding: 8px;
        border-radius: 4px;
        overflow-x: auto;

        @include themed() {
          background-color: t($grey-05);
        }

        code {
          padding: 0;
          background-color: transparent;
        }
      }

      :deep(blockquote) {
        margin: 8px 0;
        padding-inline-start: 12px;

        @include themed() {
          border-inline-start: 3px solid t($grey-20);
          color: t($grey-60);
        }
      }
    }

    .sidebar-row--conference {
      justify-content: center;
      margin-bottom: 24px;
    }

    .empty-conference {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 104px;
      height: 104px;
      border-radius: 100px;

      @include themed() {
        background: t($bg-menu-hover);
      }

      svg {
        width: 32px;
        height: 32px;
        opacity: 0.35;
      }

      :deep(.stroke) {
        @include themed() {
          stroke: t($grey-40);
        }
      }
    }

    .attendances-buttons {
      align-self: center;
    }

    .sidebar-row {
      display: flex;
      align-items: flex-start;
      column-gap: 8px;
    }

    .sidebar-icon {
      width: 20px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: 8px;

      @include themed() {
        color: t($grey-40);
      }

      svg {
        width: 16px;
        height: 16px;
      }

      :deep(.stroke) {
        @include themed() {
          stroke: t($grey-40);
        }
      }

      :deep(.fill) {
        @include themed() {
          fill: t($grey-40);
        }
      }
    }

    .sidebar-content {
      flex: 1;
      min-width: 0;
    }

    .sidebar-action {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      opacity: 0;
      padding-top: 2px;
      @include transition(opacity 150ms ease-out);

      :deep(.fill) {
        @include themed() {
          fill: t($grey-40);
        }
      }

      &:hover :deep(.fill) {
        @include themed() {
          fill: t($grey-60);
        }
      }
    }

    .sidebar-row:hover .sidebar-action {
      opacity: 1;
    }

    .sidebar-row--location {
      :deep(.icon-container) {
        display: none;
      }
    }

    .sidebar-row--events {
      :deep(.events-side) {
        display: none;
      }

      :deep(.events) {
        column-gap: 0;
      }
    }
  }

  .bottom-toast {
    display: flex;
    flex-direction: row;
    padding: 8px 24px;
    border-radius: 20px;
    position: fixed;
    bottom: 24px;
    inset-inline-start: calc(50% - 250px);
    column-gap: 4px;
    max-width: 540px;

    @include themed() {
      background-color: t($tertiary-10);
      border: 1px solid t($tertiary-20);
    }

    .text {
      margin: auto 4px auto 0;
      @extend %font-caption-regular;
    }

    button.base-button {
      flex-shrink: 0;
    }
  }
}

@keyframes wave-animate {
  0% {
    transform: scaleY(0.05);
  }
  15% {
    transform: scaleY(0.5);
  }
  25% {
    transform: scaleY(1);
  }
  35% {
    transform: scaleY(1.3);
  }
  45% {
    transform: scaleY(1);
  }
  55% {
    transform: scaleY(0.5);
  }
  65% {
    transform: scaleY(0.05);
  }
  100% {
    transform: scaleY(0.05);
  }
}
</style>

<style lang="scss">
.participant-console-theme {
  inset-inline-start: -24px;
}
</style>
